import { Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { addMilliseconds, isBefore } from 'date-fns';
import { mapValues, zipWith } from 'lodash';
import {
  AssetService,
  Loadable,
  StoreV2Subject,
  UserPreferencesService,
  WithOnDestroy,
} from '@schenck/rotec-components';
import { combineLatest, interval, Observable, Subject, BehaviorSubject } from 'rxjs';
import {
  exhaustMap,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  NotificationEntry as _NotificationEntry,
  NotificationsApi,
  NotificationsResponse,
  NotificationStatistic,
  NotificationSeverity,
} from 'src/api';
import { routes } from 'src/routes';
import { TranslateService } from '@ngx-translate/core';
import { startWith$ } from 'src/utils';

export interface NotificationEntry extends _NotificationEntry {
  machineName: string;
  plantName: string;
}

const pollInterval = 30000;
const initialDataValue: NotificationsResponse = { data: [], statistics: [] };
const lastVisitedKey = 'SC:Notifications_lastVisited';

@Injectable({ providedIn: 'root' })
export class NotificationsService extends WithOnDestroy {
  private destroy$ = new Subject();

  private response$ = interval(pollInterval).pipe(
    startWith(-1),
    switchMap((idx) =>
      this.notificationsApi.get$(
        idx === -1 ? undefined : addMilliseconds(new Date(), -pollInterval)
      )
    ),
    Loadable.filterLoaded(),
    tap(() => {
      if (!this._hasLoaded$.value) {
        this._hasLoaded$.next(true);
      }
    }),
    takeUntil(this.onDestroy$),
    shareReplay(1)
  );

  private data$ = new StoreV2Subject<NotificationsResponse>(initialDataValue);

  private lastVisit$ = this.router.events
    .pipe(
      // Filter out everything until we are on the navigation page..
      filter(isNavigationEnd),
      map((event) => event.url),
      startWith(null),
      filter(() => this.isOnNotificationsPage()),
      // .. then ignore all events and filter until we are out of the page again.
      exhaustMap(() =>
        this.router.events.pipe(
          filter(isNavigationEnd),
          filter(() => !this.isOnNotificationsPage()),
          take(1),
          // start with null to emit immediately when coming to the page
          // in order to trigger recalculation of unread notifications
          startWith(null)
        )
      )
    )
    // doing an additional pipe because the pipe typing cannot deal with this many operators
    .pipe(
      // map each event to the current date. this is the last visit
      map(() => new Date()),
      tap((date) => this.saveLastVisited(date)),
      // start with saved user preferences
      startWith$(this.getSavedLastVisited$()),
      takeUntil(this.onDestroy$),
      shareReplay(1)
    );

  private readonly _hasLoaded$ = new BehaviorSubject(false);
  hasLoaded$: Observable<boolean> = this._hasLoaded$;

  /**
   * Whether or not there was an error while loading the notifications.
   */
  hasError$: Observable<boolean> = this.response$.pipe(
    map((loadable) => Loadable.isError(loadable))
  );

  /**
   * The statistics.
   */
  statistics$: Observable<NotificationStatistic[]> = this.data$.pipe(
    map((data) => data.statistics)
  );

  /**
   * Array of notifications.
   */
  notifications$: Observable<NotificationEntry[]> = combineLatest([
    this.assetService.machines$,
    this.assetService.plantRecord$,
    this.data$,
  ]).pipe(
    map(([machines, plants, data]) => {
      return data.data.map((entry) => {
        const machine = machines.find((m) => m.realTwinId === entry.realTwinId);
        const plant = machine ? plants[machine.plantId] : undefined;
        return {
          ...entry,
          machineName:
            (machine && machine.name) || this.translate.instant('MC_Unregistered machine'),
          plantName: (plant && plant.name) || '',
        };
      });
    })
  );

  /**
   * The number of new notifications since the user last visited the tab.
   */
  nrOfNotificationsSinceLastVisit$ = combineLatest([this.lastVisit$, this.notifications$]).pipe(
    map(([lastVisit, notifications]) => {
      if (this.isOnNotificationsPage()) {
        return 0;
      }

      if (!lastVisit) {
        return notifications.length;
      }

      return notifications.filter((notification) => isBefore(lastVisit, notification.timestamp))
        .length;
    })
  );

  constructor(
    private notificationsApi: NotificationsApi,
    private router: Router,
    private assetService: AssetService,
    private translate: TranslateService,
    private userPrefService: UserPreferencesService
  ) {
    super();
    this.fillData();
  }

  private getSavedLastVisited$(): Observable<Date | undefined> {
    return this.userPrefService.get(lastVisitedKey).pipe(map((value) => value && new Date(value)));
  }

  private saveLastVisited(date: Date) {
    this.userPrefService.set(lastVisitedKey, date.toISOString());
  }

  private fillData() {
    this.response$
      .pipe(filter(Loadable.isDone), takeUntil(this.onDestroy$))
      .subscribe((response) => {
        this.data$.update((data) => {
          if (data === initialDataValue) {
            return response.result;
          }
          // incorporate new value into existing one
          // because we only get new data
          return {
            data: [...response.result.data, ...data.data],
            statistics: mergeStatistics(response.result.statistics, data.statistics),
          };
        });
      });
  }

  /**
   * Acknowledges given notification ids.
   */
  acknowledge(ids: string[]) {
    this.notificationsApi.acknowledge$(ids).subscribe((success) => {
      if (success) {
        this.data$.patch((data) => ({
          data: acknowledgeNotifications(data, ids),
        }));
      }
    });
  }

  /**
   * Acknowledges all notifications that are not severe.
   */
  acknowledgeAllUnsevere() {
    const ids = this.data$.value.data
      .filter((notification) => !isNotificationSevere(notification))
      .map((notification) => notification.id);
    this.acknowledge(ids);
  }

  private isOnNotificationsPage() {
    return this.router.url.endsWith(routes.notifications.url);
  }
}

export function isNotificationSevere(notification: _NotificationEntry): boolean {
  const severeSeverities: NotificationSeverity[] = ['Critical', 'Major'];
  return severeSeverities.includes(notification.severity);
}

function acknowledgeNotifications(
  data: NotificationsResponse,
  ids: string[]
): _NotificationEntry[] | undefined {
  return data.data.map((notification) =>
    ids.includes(notification.id) ? { ...notification, ack: true } : notification
  );
}

function isNavigationEnd(event: Event): event is NavigationEnd {
  return event instanceof NavigationEnd;
}

function mergeStatistics(
  oldS: NotificationStatistic[],
  newS: NotificationStatistic[]
): NotificationStatistic[] {
  return zipWith(oldS, newS, (o, n) => {
    return mapValues(o, (val, key) => val + n[<keyof NotificationStatistic>key]);
  });
}
